import { TreeView } from '@fluid-experimental/tree';
import { assertPrecondition } from '../../../errors';
import { NodeKind } from '..';
import { isItemNode, isMultiValuePropertyNode } from '../typeGuards';
import { getNodes, hasOnly } from './deltaUtility';
import { DeltaNodeGroup, Match, MfsChange } from './types';
import { logVerbose } from '../../../log';

/** Check whether the delta matches a propertyChanged event. */
export function matchPropertyChanged(
  view: TreeView,
  previousView: TreeView,
  delta: DeltaNodeGroup
): Match<MfsChange[]> {
  if (hasOnly(delta, NodeKind.Array, 'changed')) {
    logVerbose('matchPropertyChanged', 'NodeKind is array only, detecting change');
    return hasDeltaPropertyChange(view, delta);
  }

  assertPrecondition(hasOnly(delta, NodeKind.Item, 'changed'));
  // this can be a patch (with multiple prop updates) or single property update (via setItemProperty/deleteItemProperty)
  const changedProperties: PropertyChange = {};

  addChangedProperties(changedProperties, NodeKind.Array, 'add', delta, view);
  addChangedProperties(changedProperties, NodeKind.SingleValueNode, 'add', delta, view);
  addChangedProperties(changedProperties, NodeKind.SingleValueNode, 'remove', delta, previousView);
  addChangedProperties(changedProperties, NodeKind.Array, 'remove', delta, previousView);

  const itemNode = delta.changed[NodeKind.Item][0];
  assertPrecondition(!!itemNode && isItemNode(itemNode));
  logVerbose('matchPropertyChanged', 'item ids', { itemNodeId: itemNode.identifier, itemId: itemNode.payload.id });
  return Match.ok([
    {
      itemId: itemNode.payload.id,
      label: itemNode.payload.label,
      kind: 'propertyChanged',
      changedProperties,
    },
  ]);
}

function addChangedProperties(
  changedProperties: PropertyChange,
  nodeKind: NodeKind,
  changeKind: 'add' | 'remove',
  delta: DeltaNodeGroup,
  view: TreeView
) {
  for (const node of getNodes(delta, changeKind, nodeKind) ?? []) {
    const { label } = view.getTraitLocation(node.identifier);
    // same node appearing both in delta.added & delta.removed is an update
    changedProperties[label] = changeKind === 'add' ? 'add' : label in changedProperties ? 'update' : 'remove';
  }
}

type PropertyChange = { [propertyKey: string]: 'add' | 'remove' | 'update' };

function hasDeltaPropertyChange(view: TreeView, delta: DeltaNodeGroup): Match<MfsChange[]> {
  const propertyNode = delta.changed[NodeKind.Array][0];
  assertPrecondition(!!propertyNode && isMultiValuePropertyNode(propertyNode));
  const { parent, label } = view.getTraitLocation(propertyNode.identifier);
  const itemNode = view.getViewNode(parent);
  assertPrecondition(isItemNode(itemNode));
  logVerbose('hasDeltaPropertyChange', 'Creating delta property change match', {
    itemId: itemNode.payload.id,
    itemNodeId: itemNode.identifier,
  });
  return Match.ok([
    {
      itemId: itemNode.payload.id,
      label: itemNode.payload.label,
      kind: 'propertyChanged',
      changedProperties: { [label]: 'update' },
    },
  ]);
}
